import { useEffect } from "react";

import { type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";
import {
  VEHICLE_AHEAD_MS,
  VEHICLE_STACK_STEP_PX,
  vehicleStacksAt,
} from "@/lib/timeline/rail/RailVehicleRow/row";

interface TimelineVehicleLabelsProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
}

const nameOf = (id: string): HTMLElement | null => document.querySelector<HTMLElement>(
  `[data-marker-part="name"][data-character-id="${CSS.escape(id)}"]`,
);

/** A stack of one type of craft shares one label, in the plural, centered under the stack. */
export function TimelineVehicleLabels({
  canvas,
  layout,
  getPlayback,
}: TimelineVehicleLabelsProps) {
  useEffect(() => {
    const vehicles = layout.rail.stays.filter((stay) => stay.vehicle === true);
    if (canvas === null || vehicles.length === 0) {
      return;
    }
    const sites = [...new Set(vehicles.map((stay) => stay.rootPlaceId))];
    const nameFor = new Map(layout.markers.map((marker) => (
      [marker.characterId, marker.displayName]
    )));
    const onFrame = (): void => {
      const playheadMs = getPlayback().playheadMs;
      // Those waiting ahead are labeled as the stacks they will arrive in.
      const moments = sites.map((site) => ({ site, atMs: playheadMs }));
      for (const stay of vehicles) {
        if (stay.startMs > playheadMs && stay.startMs - playheadMs <= VEHICLE_AHEAD_MS) {
          moments.push({ site: stay.rootPlaceId, atMs: stay.startMs });
        }
      }
      for (const { site, atMs } of moments) {
        for (const stack of vehicleStacksAt(layout.rail.stays, site, atMs)) {
          for (const [index, id] of stack.entries()) {
            const label = nameOf(id);
            if (label === null) {
              continue;
            }
            const single = nameFor.get(id) ?? id;
            const text = stack.length > 1 ? `${single}s` : single;
            if (index === 0 && label.textContent !== text) {
              label.textContent = text;
            }
            label.style.filter = index === 0 ? "" : "opacity(0)";
            label.style.translate = index === 0
              ? `${((stack.length - 1) * VEHICLE_STACK_STEP_PX) / 2}px 0`
              : "";
          }
        }
      }
    };
    const afterPortraits = (): void => queueMicrotask(onFrame);
    canvas.addEventListener("timeline-frame", afterPortraits);
    return () => canvas.removeEventListener("timeline-frame", afterPortraits);
  }, [canvas, getPlayback, layout]);
  return null;
}
